import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Menu, X, Globe, DollarSign, ChevronDown, LogOut, LayoutDashboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import RegisterModal from './RegisterModal';
import { useCurrency } from '../contexts/CurrencyContext';
import { CURRENCY_CONFIG } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import { usePageAlternates } from './pageAlternates';
import { LANGS, LANG_NAMES, localizedPath } from '../config/langs';

const Header = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { clientUser, logoutClient } = useAuth();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [langMenuOpen, setLangMenuOpen] = useState(false);
    const [currencyMenuOpen, setCurrencyMenuOpen] = useState(false);
    const [registerModalOpen, setRegisterModalOpen] = useState(false);
    const { currency, changeCurrency, availableCurrencies } = useCurrency();
    const currencyRef = useRef(null);
    const langRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768) {
                setMobileMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (currencyRef.current && !currencyRef.current.contains(e.target)) {
                setCurrencyMenuOpen(false);
            }
            if (langRef.current && !langRef.current.contains(e.target)) {
                setLangMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Each language is its own URL (/en/…, /ru/…; Arabic unprefixed), so the
    // switcher is a set of real links rather than an in-place toggle. They are
    // plain <a> elements, not router links: the router's basename is the
    // language, so changing it means loading the other language's page.
    // location.pathname here is already relative to the basename.
    const location = useLocation();
    const pageAlternates = usePageAlternates();
    const hrefFor = (lng) => {
        // A page that exists in fewer languages (or at different paths, like
        // blog posts) publishes its alternates; elsewhere the path is shared.
        const path = pageAlternates ? pageAlternates[lng] : location.pathname;
        if (!path) return localizedPath('/', lng);
        // Filters (?category=, ?district=) carry over when the page is the same.
        return localizedPath(path, lng) + (path === location.pathname ? location.search : '');
    };

    const handleCurrencyChange = (code) => {
        changeCurrency(code);
        setCurrencyMenuOpen(false);
    };

    // Group currencies by region for better UX
    const currencyGroups = [
        { label: 'Gulf', codes: ['AED', 'SAR', 'KWD', 'BHD', 'QAR', 'OMR'] },
        { label: 'Major', codes: ['USD', 'EUR', 'GBP', 'CHF', 'CAD', 'AUD', 'JPY'] },
        { label: 'Other', codes: ['EGP', 'TRY', 'RUB', 'CNY', 'INR', 'PKR', 'PHP'] },
    ];

    const languages = LANGS.map((code) => ({ code, name: LANG_NAMES[code] }));

    const navItems = [
        { label: t('nav.trusted_projects'), link: '/projects' },
        { label: t('nav.off_plan'), link: '/projects?category=off-plan' },
        { label: t('nav.commercial'), link: '/projects?category=commercial' },
        { label: t('nav.launching_soon') || 'Launching Soon', link: '/projects?category=launching-soon' },
        { label: t('nav.re_sell'), link: '/re-sell' },
        { label: t('nav.approved_listings') || 'Approved Listings', link: '/approved-listings' },
        // Sitewide link to the FAQ: it is the page answer engines are most
        // likely to quote, so it should be one click from anywhere.
        { label: t('nav.faq') || 'FAQ', link: '/faq' }
    ];

    return (
        <>
            <style>{`
                .header {
                    position: fixed;
                    top: 0;
                    width: 100%;
                    z-index: 1000;
                    /* Explicit list: 'all' was animating layout properties
                       and janking the shrink-on-scroll. */
                    transition: background 0.35s ease,
                                border-color 0.35s ease,
                                backdrop-filter 0.35s ease;
                    animation: header-drop 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
                }

                @keyframes header-drop {
                    from { opacity: 0; transform: translateY(-14px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                .header.scrolled {
                    background: rgba(5, 5, 16, 0.95);
                    backdrop-filter: blur(10px);
                    border-bottom: 1px solid rgba(255, 215, 0, 0.1);
                }

                /* The 1280px body container pulls the logo and the actions in
                   toward the centre on a wide screen. The header overrides it
                   with a near-full-bleed frame so both anchor to the edges. */
                .header .container {
                    max-width: 1720px;
                    padding-inline: clamp(20px, 3.2vw, 56px);
                }

                /* Three explicit columns: the nav is centred on the header
                   itself rather than on whatever space the logo happens to
                   leave over. Sits high — light top padding, more beneath. */
                .header-inner {
                    display: grid;
                    /* minmax(0,1fr): the nav is the column that gives way
                       when space is tight, so the logo and the actions
                       keep their intrinsic width instead of compressing. */
                    grid-template-columns: auto minmax(0, 1fr) auto;
                    align-items: center;
                    gap: clamp(1.5rem, 3vw, 3.5rem);
                    padding: 0.55rem 0 0.95rem;
                    transition: padding 0.3s ease;
                }

                .header.scrolled .header-inner {
                    padding: 0.45rem 0 0.55rem;
                }

                .desktop-nav {
                    justify-self: center;
                }

                .header-actions {
                    justify-self: end;
                }

                .logo-container {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    cursor: pointer;
                    text-decoration: none;
                }

                .logo-container:hover .logo-icon {
                    transform: scale(1.06);
                }

                .logo-icon {
                    width: 56px;
                    height: 56px;
                    flex-shrink: 0;
                    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .logo-icon img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }

                .logo-text {
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                }

                .logo-text .logo-name {
                    font-size: clamp(1rem, 1.5vw, 1.45rem);
                    font-weight: 900;
                    letter-spacing: 0.14em;
                    color: #fff;
                    margin: 0;
                    line-height: 1.05;
                    white-space: nowrap;
                }

                .logo-text span {
                    font-size: clamp(0.55rem, 0.75vw, 0.72rem);
                    color: var(--color-neon-blue);
                    font-weight: 600;
                    letter-spacing: 0.32em;
                    display: block;
                    white-space: nowrap;
                    margin-top: 2px;
                }

                /* Desktop Navigation */
                .desktop-nav {
                    display: flex;
                    gap: clamp(1.4rem, 2.4vw, 2.9rem);
                    align-items: center;
                    flex-wrap: nowrap;
                }

                .nav-link {
                    color: #fff;
                    text-decoration: none;
                    font-size: clamp(0.75rem, 0.85vw, 0.88rem);
                    font-weight: 600;
                    letter-spacing: 0.06em;
                    transition: color 0.25s ease;
                    text-transform: uppercase;
                    white-space: nowrap;
                    position: relative;
                    padding: 8px 2px;
                }

                /* Underline grows from the centre out, and is inset-based so
                   it works unchanged in RTL (left:0 did not). */
                .nav-link::after {
                    content: '';
                    position: absolute;
                    inset-inline: 50%;
                    bottom: 0;
                    height: 2px;
                    background: linear-gradient(90deg,
                        rgba(255, 215, 0, 0.3), var(--color-gold), rgba(255, 215, 0, 0.3));
                    border-radius: 2px;
                    transition: inset-inline 0.34s cubic-bezier(0.16, 1, 0.3, 1),
                                opacity 0.34s ease;
                    opacity: 0;
                }

                .nav-link:hover,
                .nav-link:focus-visible {
                    color: var(--color-gold);
                }

                .nav-link:hover::after,
                .nav-link:focus-visible::after {
                    inset-inline: 0;
                    opacity: 1;
                }

                /* Press feedback. Every control in the header dips slightly on
                   pointer-down, which is what makes a click feel answered. */
                .nav-link:active {
                    transform: translateY(1px);
                }

                /* Header Actions */
                .header-actions {
                    display: flex;
                    align-items: center;
                    gap: clamp(0.9rem, 1.5vw, 1.6rem);
                    flex-shrink: 0;
                }

                .action-btn {
                    background: none;
                    border: none;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    font-size: 0.9rem;
                    position: relative;
                    padding: 7px 10px;
                    border-radius: var(--r-pill);
                    transition: background 0.25s ease,
                                color 0.25s ease,
                                transform 0.16s cubic-bezier(0.16, 1, 0.3, 1);
                }

                /* :not(.login-btn) — the login pill carries both classes, and
                   this generic hover was overwriting its gold gradient with a
                   translucent white, making it vanish against the header. */
                .action-btn:not(.login-btn):hover {
                    background: rgba(255, 255, 255, 0.08);
                    color: var(--color-gold);
                }

                .action-btn:not(.login-btn):active { transform: scale(0.94); }

                .icon-btn { padding: 8px; }

                .logout-btn { opacity: 0.65; }
                .logout-btn:hover { opacity: 1; color: #ff8a8a; }
                .logout-btn:hover { background: rgba(255, 120, 120, 0.12); }

                /* ── Login: the one button that gets special treatment ──────
                   A gold pill that lifts and glows, with a light sheen that
                   sweeps across on hover. The sweep is a child element rather
                   than a moving background-position, so it composites on the
                   GPU and never repaints the label. */
                .login-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 9px;
                    position: relative;
                    overflow: hidden;
                    isolation: isolate;
                    padding: 10px 24px;
                    border-radius: var(--r-pill);

                    /* The label is two words in most languages — "LOG IN",
                       "تسجيل دخول", "Войти" — and without these it wrapped to
                       two lines as soon as the header row got tight. */
                    white-space: nowrap;
                    flex-shrink: 0;

                    /* Brushed gold rather than a flat two-stop ramp: a light
                       top edge, the brand gold through the middle, and a
                       deeper base, so the pill reads as a struck object. */
                    background: linear-gradient(160deg,
                        #FFEFA8 0%,
                        #FFD84D 34%,
                        var(--color-gold) 62%,
                        #E3A70B 100%);
                    color: #241B00;
                    font-weight: 800;
                    font-size: 0.82rem;
                    letter-spacing: 0.05em;
                    line-height: 1;
                    text-decoration: none;

                    /* Inset hairline on top + tight contact shadow + soft glow. */
                    box-shadow:
                        inset 0 1px 0 rgba(255, 255, 255, 0.65),
                        inset 0 -1px 0 rgba(140, 95, 0, 0.35),
                        0 2px 6px rgba(0, 0, 0, 0.28),
                        0 6px 20px rgba(255, 200, 40, 0.26);
                    transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1),
                                box-shadow 0.28s ease,
                                filter 0.28s ease;
                }

                .login-btn svg { flex-shrink: 0; }

                .login-btn:hover {
                    transform: translateY(-2px);
                    color: #241B00;
                    background: linear-gradient(160deg,
                        #FFEFA8 0%,
                        #FFD84D 34%,
                        var(--color-gold) 62%,
                        #E3A70B 100%);
                    filter: saturate(1.08) brightness(1.04);
                    box-shadow:
                        inset 0 1px 0 rgba(255, 255, 255, 0.75),
                        inset 0 -1px 0 rgba(140, 95, 0, 0.3),
                        0 4px 10px rgba(0, 0, 0, 0.3),
                        0 12px 30px rgba(255, 200, 40, 0.45);
                }

                .login-btn:active {
                    transform: translateY(1px) scale(0.975);
                    box-shadow:
                        inset 0 2px 4px rgba(140, 95, 0, 0.4),
                        0 1px 3px rgba(0, 0, 0, 0.3);
                    transition-duration: 0.08s;
                }

                .login-btn-sheen {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    width: 45%;
                    left: -60%;
                    z-index: -1;
                    background: linear-gradient(100deg,
                        transparent, rgba(255, 255, 255, 0.75), transparent);
                    transform: skewX(-18deg);
                    pointer-events: none;
                }

                .login-btn:hover .login-btn-sheen {
                    animation: login-sheen 0.75s cubic-bezier(0.3, 0, 0.2, 1);
                }

                @keyframes login-sheen {
                    from { left: -60%; }
                    to   { left: 130%; }
                }

                /* Language Dropdown */
                .lang-dropdown {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    background: rgba(20, 20, 35, 0.95);
                    border: 1px solid rgba(255, 215, 0, 0.2);
                    border-radius: 8px;
                    padding: 8px 0;
                    min-width: 120px;
                    margin-top: 10px;
                    backdrop-filter: blur(10px);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
                    display: flex;
                    flex-direction: column;
                }

                .lang-item {
                    display: block;
                    text-decoration: none;
                    padding: 8px 16px;
                    color: #fff;
                    text-align: start;
                    background: none;
                    border: none;
                    cursor: pointer;
                    transition: background 0.2s;
                    font-size: 0.9rem;
                }

                .lang-item:hover {
                    background: rgba(255, 215, 0, 0.1);
                    color: var(--color-gold);
                }

                /* Currency Dropdown */
                .currency-dropdown {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    background: rgba(15, 15, 30, 0.98);
                    border: 1px solid rgba(255, 215, 0, 0.2);
                    border-radius: 12px;
                    padding: 8px;
                    min-width: 260px;
                    max-height: 400px;
                    overflow-y: auto;
                    margin-top: 10px;
                    backdrop-filter: blur(15px);
                    box-shadow: 0 8px 32px rgba(0,0,0,0.6);
                    z-index: 1001;
                }

                .currency-dropdown::-webkit-scrollbar {
                    width: 4px;
                }
                .currency-dropdown::-webkit-scrollbar-thumb {
                    background: rgba(255,215,0,0.3);
                    border-radius: 4px;
                }

                .currency-group-label {
                    padding: 6px 12px;
                    color: rgba(255,215,0,0.6);
                    font-size: 0.7rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }

                .currency-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 12px;
                    color: #fff;
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 0.85rem;
                    border-radius: 8px;
                    width: 100%;
                    text-align: left;
                }

                .currency-item:hover {
                    background: rgba(255, 215, 0, 0.1);
                }

                .currency-item.active {
                    background: rgba(255, 215, 0, 0.15);
                    color: var(--color-gold);
                }

                .currency-item-flag {
                    font-size: 1.2rem;
                    width: 24px;
                    text-align: center;
                }

                .currency-item-code {
                    font-weight: 700;
                    min-width: 36px;
                }

                .currency-item-name {
                    color: rgba(255,255,255,0.5);
                    font-size: 0.75rem;
                }

                .currency-btn {
                    background: rgba(255,255,255,0.06);
                    border: 1px solid rgba(255,255,255,0.1);
                    color: #fff;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    cursor: pointer;
                    font-size: 0.8rem;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-weight: 600;
                }

                .currency-btn:hover {
                    border-color: rgba(255, 215, 0, 0.6);
                    color: var(--color-gold);
                    background: rgba(255, 215, 0, 0.1);
                }

                .currency-btn:active { transform: scale(0.94); }

                .currency-btn {
                    transition: background 0.25s ease, border-color 0.25s ease,
                                color 0.25s ease,
                                transform 0.16s cubic-bezier(0.16, 1, 0.3, 1);
                }

                /* Dropdown rows answer the pointer too. */
                .lang-item:active,
                .currency-item:active { transform: scale(0.97); }

                .lang-item,
                .currency-item {
                    transition: background 0.2s ease, color 0.2s ease,
                                transform 0.14s ease;
                }

                /* Mobile Menu Button */
                .mobile-menu-btn {
                    display: none;
                    background: none;
                    border: none;
                    color: #fff;
                    cursor: pointer;
                    padding: 8px;
                }

                /* Mobile Navigation */
                .mobile-nav {
                    display: none;
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(5, 5, 16, 0.98);
                    z-index: 2000;
                    flex-direction: column;
                    padding: 80px 24px 40px;
                    backdrop-filter: blur(20px);
                    overflow-y: auto;
                }

                .mobile-nav.open {
                    display: flex;
                }

                .mobile-nav-close {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    background: none;
                    border: none;
                    color: #fff;
                    cursor: pointer;
                }

                .mobile-nav-links {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                    margin-bottom: 2rem;
                }

                .mobile-nav-link {
                    color: #fff;
                    text-decoration: none;
                    font-size: 1.15rem;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    transition: color 0.3s;
                    padding: 10px 0;
                    border-bottom: 1px solid rgba(255,255,255,0.1);
                    text-transform: uppercase;
                }

                .mobile-nav-link:hover {
                    color: var(--color-gold);
                }

                .mobile-nav-link {
                    transition: color 0.25s ease, transform 0.16s ease,
                                padding-inline-start 0.25s ease;
                }

                .mobile-nav-link:active {
                    transform: scale(0.985);
                    color: var(--color-gold);
                }

                .mobile-menu-btn,
                .mobile-nav-close {
                    transition: transform 0.18s cubic-bezier(0.16, 1, 0.3, 1),
                                color 0.2s ease;
                }

                .mobile-menu-btn:active,
                .mobile-nav-close:active { transform: scale(0.88); }

                .mobile-lang-btn {
                    transition: border-color 0.25s ease, color 0.25s ease,
                                background 0.25s ease, transform 0.16s ease;
                }

                .mobile-lang-btn:active { transform: scale(0.94); }

                .mobile-nav-actions {
                    margin-top: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .mobile-lang-options {
                    display: flex;
                    gap: 10px;
                    justify-content: center;
                    margin-top: 1rem;
                    flex-wrap: wrap;
                }
                
                .mobile-lang-btn {
                    display: inline-block;
                    text-decoration: none;
                    padding: 8px 16px;
                    border: 1px solid rgba(255,255,255,0.2);
                    border-radius: 20px;
                    color: #fff;
                    background: transparent;
                }

                .mobile-lang-btn.active {
                    border-color: var(--color-gold);
                    color: var(--color-gold);
                }

                /* Responsive */
                @media (max-width: 900px) {
                    .desktop-nav {
                        display: none;
                    }

                    .mobile-menu-btn {
                        display: flex;
                    }

                    .login-btn {
                        display: none;
                    }
                }

                @media (max-width: 600px) {
                    .header-actions {
                        gap: 0.4rem;
                    }

                    .currency-btn {
                        padding: 5px 8px !important;
                        font-size: 0.7rem !important;
                    }

                    .lang-container .action-btn span {
                        display: none;
                    }

                    .logo-text .logo-name {
                        font-size: 1rem !important;
                    }

                    .logo-text span {
                        font-size: 0.5rem !important;
                        letter-spacing: 2px !important;
                    }
                }

                @media (max-width: 480px) {
                    .logo-icon {
                        width: 28px;
                        height: 28px;
                    }

                    .header-actions {
                        gap: 0.3rem;
                    }

                    .currency-btn {
                        padding: 4px 6px !important;
                        font-size: 0.65rem !important;
                        gap: 3px !important;
                    }

                    .currency-btn .chevron-down {
                        display: none;
                    }
                }
            `}</style>

            <header className={`header ${scrolled ? 'scrolled' : ''}`}>
                <div className="container">
                    <div className="header-inner">
                        {/* Logo */}
                        <Link to="/" className="logo-container">
                            <div className="logo-icon">
                                <img src="/logo-256.webp" alt="Al-Zaeem Logo" width={128} height={128} decoding="async" fetchPriority="high" />
                            </div>
                            <div className="logo-text">
                                {/* Not an h1: this sits on every page, and each page owns its own h1. */}
                                <strong className="logo-name">AL-ZAEEM</strong>
                                <span>AL-AINAWI</span>
                            </div>
                        </Link>

                        {/* Desktop Nav */}
                        <nav className="desktop-nav">
                            {navItems.map((item) => (
                                <Link key={item.label} to={item.link} className="nav-link">
                                    {item.label}
                                </Link>
                            ))}
                        </nav>

                        {/* Actions */}
                        <div className="header-actions">
                            {/* Currency Selector */}
                            <div ref={currencyRef} style={{ position: 'relative' }}>
                                <button
                                    className="currency-btn"
                                    aria-label={t('nav.aria.currency')}
                                    aria-expanded={currencyMenuOpen}
                                    onClick={() => { setCurrencyMenuOpen(!currencyMenuOpen); setLangMenuOpen(false); }}
                                >
                                    <span>{CURRENCY_CONFIG[currency]?.flag}</span>
                                    <span>{currency}</span>
                                    <ChevronDown size={14} style={{ opacity: 0.6 }} />
                                </button>
                                {currencyMenuOpen && (
                                    <div className="currency-dropdown">
                                        {currencyGroups.map((group) => (
                                            <div key={group.label}>
                                                <div className="currency-group-label">{group.label}</div>
                                                {group.codes.filter(code => availableCurrencies.includes(code)).map((code) => (
                                                    <button
                                                        key={code}
                                                        className={`currency-item ${currency === code ? 'active' : ''}`}
                                                        onClick={() => handleCurrencyChange(code)}
                                                    >
                                                        <span className="currency-item-flag">{CURRENCY_CONFIG[code]?.flag}</span>
                                                        <span className="currency-item-code">{code}</span>
                                                        <span className="currency-item-name">{CURRENCY_CONFIG[code]?.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Language Switcher */}
                            <div ref={langRef} className="lang-container" style={{ position: 'relative' }}>
                                <button
                                    className="action-btn"
                                    aria-label={t('nav.aria.language')}
                                    aria-expanded={langMenuOpen}
                                    onClick={() => { setLangMenuOpen(!langMenuOpen); setCurrencyMenuOpen(false); }}
                                >
                                    <Globe size={20} />
                                    <span style={{ textTransform: 'uppercase' }}>{i18n.language}</span>
                                </button>
                                {/* Always in the DOM so the links are crawlable;
                                    only its visibility follows the toggle. */}
                                <nav
                                    className="lang-dropdown"
                                    aria-label={t('nav.aria.language')}
                                    style={langMenuOpen ? undefined : { display: 'none' }}
                                >
                                    {languages.map((lang) => (
                                        <a
                                            key={lang.code}
                                            className="lang-item"
                                            href={hrefFor(lang.code)}
                                            hrefLang={lang.code}
                                            lang={lang.code}
                                            aria-current={i18n.language === lang.code ? 'true' : undefined}
                                        >
                                            {lang.name}
                                        </a>
                                    ))}
                                </nav>
                            </div>



                            {clientUser ? (
                                <>
                                    <Link
                                        to="/my-listings"
                                        className="action-btn login-btn"
                                    >
                                        <span className="login-btn-sheen" aria-hidden="true" />
                                        <LayoutDashboard size={18} />
                                        <span>{t('nav.my_listings') || 'My Listings'}</span>
                                    </Link>
                                    <button
                                        className="action-btn icon-btn logout-btn"
                                        onClick={() => { logoutClient(); navigate('/'); }}
                                        title={t('nav.aria.logout')}
                                        aria-label={t('nav.aria.logout')}
                                    >
                                        <LogOut size={18} />
                                    </button>
                                </>
                            ) : (
                                <Link
                                    to="/client/login"
                                    className="action-btn login-btn"
                                >
                                    <span className="login-btn-sheen" aria-hidden="true" />
                                    <User size={18} />
                                    <span>{t('nav.login') || 'LOGIN'}</span>
                                </Link>
                            )}

                            <button
                                className="mobile-menu-btn"
                                aria-label={t('nav.aria.open_menu')}
                                aria-expanded={mobileMenuOpen}
                                onClick={() => setMobileMenuOpen(true)}
                            >
                                <Menu size={28} />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Navigation */}
            <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
                <button
                    className="mobile-nav-close"
                    aria-label={t('nav.aria.close_menu')}
                    onClick={() => setMobileMenuOpen(false)}
                >
                    <X size={32} />
                </button>

                <div className="mobile-nav-links">
                    {navItems.map((item) => (
                        <Link
                            key={item.label}
                            to={item.link}
                            className="mobile-nav-link"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            {item.label}
                        </Link>
                    ))}
                </div>

                <div className="mobile-nav-actions">


                    {clientUser ? (
                        <>
                            <Link
                                to="/my-listings"
                                className="btn-primary"
                                onClick={() => setMobileMenuOpen(false)}
                                style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', textDecoration: 'none' }}
                            >
                                <LayoutDashboard size={20} />
                                {t('nav.my_listings') || 'My Listings'}
                            </Link>
                            <button
                                onClick={() => { logoutClient(); setMobileMenuOpen(false); navigate('/'); }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center',
                                    padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '10px', color: '#fff', cursor: 'pointer', fontSize: '1rem'
                                }}
                            >
                                <LogOut size={20} />
                                {t('nav.logout') || 'Logout'}
                            </button>
                        </>
                    ) : (
                        <Link
                            to="/client/login"
                            className="btn-primary"
                            onClick={() => setMobileMenuOpen(false)}
                            style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', textDecoration: 'none' }}
                        >
                            <User size={20} />
                            {t('nav.login') || 'LOGIN'}
                        </Link>
                    )}

                    <div className="mobile-lang-options">
                        {languages.map((lang) => (
                            <a
                                key={lang.code}
                                className={`mobile-lang-btn ${i18n.language === lang.code ? 'active' : ''}`}
                                href={hrefFor(lang.code)}
                                hrefLang={lang.code}
                                lang={lang.code}
                                aria-current={i18n.language === lang.code ? 'true' : undefined}
                            >
                                {lang.name}
                            </a>
                        ))}
                    </div>

                    {/* Mobile Currency Selector */}
                    <div style={{ marginTop: '1.5rem' }}>
                        <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '0.75rem', textAlign: 'center' }}>
                            {t('nav.currency') || 'Currency'}
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            padding: '4px'
                        }}>
                            {availableCurrencies.map((code) => (
                                <button
                                    key={code}
                                    onClick={() => { changeCurrency(code); setMobileMenuOpen(false); }}
                                    style={{
                                        padding: '6px 12px',
                                        border: currency === code ? '1px solid var(--color-gold)' : '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '8px',
                                        color: currency === code ? 'var(--color-gold)' : '#fff',
                                        background: currency === code ? 'rgba(255,215,0,0.1)' : 'rgba(255,255,255,0.05)',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <span>{CURRENCY_CONFIG[code]?.flag}</span>
                                    <span>{code}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Register Modal */}
            <RegisterModal
                isOpen={registerModalOpen}
                onClose={() => setRegisterModalOpen(false)}
            />
        </>
    );
};

export default Header;
